import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { apiClient, apiClientRaw } from "../../../api/httpClient";
import { toast } from "react-toastify";
import { clearAuthData, updateTocAccepted } from "../../../store/slices/authSlice";
import { resetLawfirm } from "../../../store/slices/lawFirmSlice";
import { clearClientSession } from "../../../globals/utils/authSession";
import { persistor } from "../../../store/store";

/**
 * Shared Terms of Use / Privacy Policy accept-reject dialog.
 * Shown when the authenticated user has not yet accepted the TOC.
 */
export default function TermsOfUseDialog() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => state.auth.user);

  const [openTerms, setOpenTerms] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (userdetails?.toc_accepted === false) {
      setOpenTerms(true);
    }
  }, [userdetails]);

  const handleTabChange = (_event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAccept = async () => {
    try {
      await apiClient("/toc-accept", "PATCH");
      toast.success("Terms accepted successfully.");
      dispatch(updateTocAccepted());
      setOpenTerms(false);
    } catch (error) {
      console.error("Failed to accept terms:", error);
      toast.error("Failed to accept terms. Please try again.");
    }
  };

  const handleReject = async () => {
    setLoggingOut(true);
    dispatch(clearAuthData());
    dispatch(resetLawfirm());
    clearClientSession();
    localStorage.removeItem("count");
    if (persistor?.purge) {
      persistor.purge();
    }
    navigate("/");
    toast.info("You must accept the Terms to continue. You have been logged out.");

    try {
      const response = await apiClientRaw("/logout/", "POST", {});

      if (response.status === 205) {
        // Already cleared above
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Dialog
      open={openTerms}
      onClose={() => {}}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        paper: {
          style: {
            borderRadius: "16px",
            padding: "10px",
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
        Website Terms of Use
      </DialogTitle>
      <Divider />
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="Website Terms of Use" />
        <Tab label="Privacy Policy for California Residents" />
      </Tabs>
      <DialogContent
        dividers
        sx={{ height: "70vh", p: 2, backgroundColor: "#fafafa" }}
      >
        {selectedTab === 0 && (
          <iframe
            src="/toc.pdf"
            title="Terms of Use PDF"
            style={{ width: "100%", height: "100%", border: "none", borderRadius: "4px" }}
          />
        )}
        {selectedTab === 1 && (
          <iframe
            src="/ccpa.pdf"
            title="Privacy Policy PDF"
            style={{ width: "100%", height: "100%", border: "none", borderRadius: "4px" }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Documents provided by Vermost & Associates, LLC
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            onClick={handleReject}
            variant="outlined"
            color="error"
            disabled={loggingOut}
          >
            Reject
          </Button>
          <Button onClick={handleAccept} variant="contained" color="primary">
            Accept
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
